import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mergeMap } from 'rxjs';

import { safeMap } from '../effects.helpers';

import { AppleDeviceProfileApiActions, AppleDeviceProfileResolverActions } from './apple-device-profile.actions';
import { APPLE_DEVICE_PROFILE_CONFIGS } from './apple-device-profile.registry';
import {
  AppleDeviceProfileEntry,
  AppleDeviceProfileType,
  AppleDeviceProfileTypeConfig,
  ProfileDataType,
} from './apple-device-profile.types';

@Injectable()
export class AppleDeviceProfileEffects {
  private readonly actions$ = inject(Actions);
  private readonly configs = inject(APPLE_DEVICE_PROFILE_CONFIGS);

  private readonly configMap: Map<AppleDeviceProfileType, AppleDeviceProfileTypeConfig>;

  constructor() {
    this.configMap = new Map(this.configs.map((c) => [c.profileType, c]));
  }

  profileRequestedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppleDeviceProfileResolverActions.profileRequested),
      mergeMap(({ udid, profileType }) => {
        const config = this.configMap.get(profileType);

        if (!config) {
          return [
            {
              ...AppleDeviceProfileApiActions.profileLoadedError({
                udid,
                profileType,
                error: { message: `Unknown profile type: ${profileType}` },
              }),
              toast: {
                message: `[Error]: Unknown profile type: ${profileType}`,
                toastType: 'error',
                showNotification: true,
              },
            },
          ];
        }

        return safeMap(
          config.apiFn(udid),
          (result) => {
            const entries = this.mapToEntries(udid, config, result);
            return AppleDeviceProfileApiActions.profileLoaded({ entries });
          },
          (error) => AppleDeviceProfileApiActions.profileLoadedError({ udid, profileType, error })
        );
      })
    );
  });

  private mapToEntries<T extends AppleDeviceProfileType>(
    udid: string,
    config: AppleDeviceProfileTypeConfig<T>,
    result: ProfileDataType<T> | ProfileDataType<T>[]
  ): AppleDeviceProfileEntry<T>[] {
    const items = Array.isArray(result) ? result : [result];

    if (config.isMultiItem && config.getItemId) {
      const getItemId = config.getItemId;
      return items.map((item) => ({
        udid,
        profileType: config.profileType,
        itemId: getItemId(item),
        data: item,
      }));
    }

    // Single-item profile: one entry per device
    return [
      {
        udid,
        profileType: config.profileType,
        data: items[0],
      },
    ];
  }
}
